import React from 'react'

import PaymentMethodSelectionPill from '@src/pages/FiatExchange/components/PaymentMethodSelectionPill'
import { RampDirection } from '@src/pages/FiatExchange/components/ToggleRampDirection'
import { DEFAULT_ONRAMP_FIAT_CURRENCY, DEFAULT_ONRAMP_TOKEN } from '@src/pages/FiatExchange/config'
import { useOfframpPaymentOptions } from '@src/pages/FiatExchange/hooks/useOfframpPaymentOptions'
import { useOnrampPaymentOptions } from '@src/pages/FiatExchange/hooks/useOnrampPaymentOptions'
import { PaymentMethod } from '@src/types/coinbase'

interface PaymentMethodSelectionContainerProps {
    rampDirection: RampDirection
    selectedPaymentMethod?: PaymentMethod
    onPaymentMethodChanged: (method?: PaymentMethod) => void
    sellAsset?: string
    sellCurrency?: string
    disabled?: boolean
    country?: string
}

/**
 * Container for payment method selection that loads and displays available payment methods
 * based on the current ramp direction (onramp/offramp)
 */
const PaymentMethodSelectionContainer: React.FC<PaymentMethodSelectionContainerProps> = ({
    rampDirection,
    selectedPaymentMethod,
    onPaymentMethodChanged,
    sellAsset,
    sellCurrency,
    disabled = false,
    country = 'US',
}) => {
    // Pre-fetch onramp payment methods with the default fiat currency
    const { data: prefetchedOnrampMethods = [], isLoading: isPrefetchingOnramp } = useOnrampPaymentOptions(
        country,
        DEFAULT_ONRAMP_FIAT_CURRENCY.symbol, // Use default currency for pre-fetch
        rampDirection === 'ONRAMP' && !disabled, // Enable if in onramp mode
        true // Always prefetch
    )

    // Fetch currency-specific onramp payment methods when a currency is selected
    const { data: currencySpecificOnrampMethods = [], isLoading: isCurrencySpecificOnrampLoading } =
        useOnrampPaymentOptions(
            country,
            sellCurrency, // Pass the selected fiat currency
            rampDirection === 'ONRAMP' && !disabled && !!sellCurrency,
            false // Don't prefetch, get specific currency methods
        )

    // Pre-fetch offramp payment methods with the default crypto asset
    const { data: prefetchedOfframpMethods = [], isLoading: isPrefetchingOfframp } = useOfframpPaymentOptions(
        country,
        undefined, // subdivision
        DEFAULT_ONRAMP_TOKEN.symbol, // Use default token for pre-fetch
        rampDirection === 'OFFRAMP' && !disabled, // Enable if in offramp mode
        true // Always prefetch
    )

    // Fetch asset-specific offramp payment methods when an asset is selected
    const { data: assetSpecificOfframpMethods = [], isLoading: isAssetSpecificOfframpLoading } =
        useOfframpPaymentOptions(
            country,
            undefined, // subdivision
            sellAsset, // crypto asset for offramp
            rampDirection === 'OFFRAMP' && !disabled && !!sellAsset,
            false // Don't prefetch, get specific asset methods
        )

    // Determine which payment methods to show based on ramp direction and what data we have
    let paymentMethods = []
    let isLoading = false

    if (rampDirection === 'ONRAMP') {
        // Use currency-specific methods if a currency is selected and methods are available
        // Otherwise fall back to prefetched methods
        if (sellCurrency && currencySpecificOnrampMethods.length > 0) {
            paymentMethods = currencySpecificOnrampMethods
            isLoading = isCurrencySpecificOnrampLoading
        } else {
            paymentMethods = prefetchedOnrampMethods
            isLoading = isPrefetchingOnramp
        }
    } else {
        // OFFRAMP
        // Use asset-specific methods if an asset is selected and methods are available
        // Otherwise fall back to prefetched methods
        if (sellAsset && assetSpecificOfframpMethods.length > 0) {
            paymentMethods = assetSpecificOfframpMethods
            isLoading = isAssetSpecificOfframpLoading
        } else {
            paymentMethods = prefetchedOfframpMethods
            isLoading = isPrefetchingOfframp
        }
    }

    // Determine the label text based on ramp direction
    const labelText = rampDirection === 'ONRAMP' ? 'Payment Method' : 'Cashout Method'

    return (
        <div className='w-full'>
            <div className='mb-2'>
                <label className='text-mach-color-text-secondary mb-1 block text-sm font-medium'>{labelText}</label>
                <PaymentMethodSelectionPill
                    paymentMethod={selectedPaymentMethod}
                    onChange={onPaymentMethodChanged}
                    availablePaymentMethods={paymentMethods}
                    isLoading={isLoading || disabled}
                    rampDirection={rampDirection}
                    fiatCurrencySymbol={sellCurrency}
                    tokenSymbol={sellAsset}
                />
            </div>
        </div>
    )
}

export default PaymentMethodSelectionContainer
